<template>
  <span class="nebTooltip"
    :class="position"
  >
    <span @mouseenter="onMouseEnter"
      @mouseleave="show = false"
      style="display:contents;"
      ref="contentWrapper"
    >
      <slot></slot>
    </span>

    <div class="nebTooltip-tooltip"
      :style="{
        top,
        left,
        visibility: forceOpen || show ? 'visible' : 'hidden'
      }"
    >
      <div class="nebTooltip-tooltip-wrapper"
        ref="tooltip"
      >
        <span :style="spanStyle">{{ content }}</span>
      </div>
    </div>
  </span>
</template>

<script>
export default {
  name: 'NebTooltip',

  props: {
    content: {
      type: String,
      default: 'Tooltip'
    },
    position: {
      type: String,
      default: 'top'
    },
    forceOpen: {
      type: Boolean,
      default: false
    }
  },

  data: () => ({
    show: false,
    top: '0px',
    left: '0px'
  }),

  computed: {
    spanStyle () {
      // Because of the CSS positioning rules required to make the tooltip work, we need to manually set the width of
      // the span, otherwise the tooltip will only ever be as wide as the element it wraps.
      const testSpan = document.createElement('span')
      testSpan.style.opacity = '0%'
      const testText = document.createTextNode(this.content)

      testSpan.appendChild(testText)

      document.body.appendChild(testSpan)

      const width = testSpan.offsetWidth

      const style = {
        maxWidth: width <= 155 ? `${width}px` : '155px'
      }

      document.body.removeChild(testSpan)

      return style
    }
  },

  methods: {
    updatePosition () {
      const wrappedEl = this.$refs.contentWrapper.firstChild
      const wrappedDimensions = wrappedEl.getBoundingClientRect()
      const tooltipDimensions = this.$refs.tooltip.getBoundingClientRect()
      const documentWidth = document.body.clientWidth

      let top, left

      switch (this.position) {
        case 'top':
          top = (wrappedDimensions.top + document.documentElement.scrollTop) - (tooltipDimensions.height + 10)
          // (content left + (content width / 2)) - (tooltip width / 2)
          left = (wrappedDimensions.left + (wrappedDimensions.width / 2)) -
            (tooltipDimensions.width / 2)
          break
        case 'bottom':
          top = wrappedDimensions.bottom + 10 + document.documentElement.scrollTop
          left = (wrappedDimensions.left + (wrappedDimensions.width / 2)) -
            (tooltipDimensions.width / 2)
          break
        case 'right':
          // (content top + (content height / 2)) - (tooltip height / 2)
          top = ((wrappedDimensions.top + document.documentElement.scrollTop) + (wrappedDimensions.height / 2)) - (tooltipDimensions.height / 2)
          left = wrappedDimensions.left + wrappedDimensions.width + 10
          break
        case 'left':
          top = ((wrappedDimensions.top + document.documentElement.scrollTop) + (wrappedDimensions.height / 2)) - (tooltipDimensions.height / 2)
          left = wrappedDimensions.left - (tooltipDimensions.width + 10)
      }

      this.top = `${top}px`

      if (left <= 10) {
        this.left = '10px'
      } else if (left + tooltipDimensions.width >= (documentWidth - 10)) {
        this.left = `${documentWidth - tooltipDimensions.width - 10}px`
      } else {
        this.left = `${left}px`
      }
    },
    onMouseEnter () {
      this.updatePosition()
      this.show = true
    }
  },

  watch: {
    position () { this.updatePosition() }
  },

  mounted () {
    this.updatePosition()

    window.addEventListener('resize', this.updatePosition)
  },

  beforeDestroy () {
    window.removeEventListener('resize', this.updatePosition)
  }
}
</script>

<style lang="scss">
@import '../../style.scss';

.nebTooltip {
  position: relative;
  overflow: visible;
  display: contents;

  &.top {
    .nebTooltip-tooltip {
      // transform: translateX(-25%);
      // bottom: calc(100% + 10px);

      &-wrapper {
        &:before {
          bottom: -5px;
          left: calc(50% - 5px);
        }

        &:after {
          bottom: 0;
          left: 0;
        }
      }
    }
  }

  &.bottom {
    .nebTooltip-tooltip {
      // top: calc(100% + 10px);
      // transform: translateX(-25%);

      &-wrapper {
        &:before {
          top: -5px;
          left: calc(50% - 5px);
        }

        &:after {
          top: 0;
          left: 0;
        }
      }
    }
  }

  &.left {
    .nebTooltip-tooltip {
      // top: 0;
      // right: calc(100% + 10px);
      // transform: translateY(-42.5%);

      &-wrapper {
        &:before {
          right: -5px;
          top: calc(50% - 5px);
        }

        &:after {
          height: 100%;
          width: 15px;
          right: 0;
          top: 0;
        }
      }
    }
  }

  &.right {
    .nebTooltip-tooltip {
      // left: calc(100% + 10px);
      // top: 0;
      // transform: translateY(-42.5%);

      &-wrapper {
        &:before {
          left: -5px;
          top: calc(50% - 5px);
        }

        &:after {
          height: 100%;
          width: 15px;
          left: 0;
          top: 0;
        }
      }
    }
  }

  &-tooltip {
    position: absolute;
    max-width: 185px;
    z-index: 999;

    &-wrapper {
      position: relative;
      max-width: 185px;
      background-color: $white;
      border-radius: 3px;
      padding: 15px;
      color: $slate-grey;
      font-size: 12px;
      font-family: $defaultFont;
      box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.5);
      z-index: 1000;
      box-sizing: border-box;

      span {
        overflow: hidden;
        display: inline-block;
        font-size: 12px;
        font-family: $defaultFont;
        font-weight: 300;
        line-height: normal;
        word-wrap: break-word;
        white-space: pre-wrap;
      }

      &:before {
        content: "";
        height: 10px;
        width: 10px;
        background-color: $white;
        position: absolute;
        z-index: 999;
        transform: rotate(45deg);
        box-shadow: inherit;
        display: block;
        box-sizing: border-box;
      }

      &:after {
        content: "";
        height: 15px;
        width: 100%;
        background-color: $white;
        box-shadow: none;
        z-index: 1001;
        display: block;
        box-sizing: border-box;
        position: absolute;
      }
    }
  }
}
</style>
